import "./RecoverAccountModal.scss";
import React, { useEffect, useState } from "react";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonLoading,
  IonModal,
  IonText,
  useIonViewWillEnter,
  IonFooter,
} from "@ionic/react";
import logoAbaxxIcon from "../../assets/svg/logoAbaxx.svg";
import p from "../../../package.json";
import moment from "moment";
import { v4 } from "uuid";
import { getUserByIdentity, requestRecoverAccount } from "../../api/user";
import {
  ShowToastErrorMessage,
  ShowToastSuccessMessage,
} from "../../utils/helpers/ToastMessage";
import { useTracked } from "../../state";
import CheckEmailModal from "../CheckEmailModal";
import { formatDeletedAccountEmail } from "../../utils/helpers/String";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedEmail?: string;
  setSelectedEmail?: any;
}

const RecoverAccountModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  selectedEmail,
  setSelectedEmail,
}) => {
  const [state, dispatch] = useTracked();
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenCheckEmail, setIsOpenCheckEmail] = useState<boolean>(false);
  const [recoveredIdentity, setRecoveredIdentity] = useState<string>("");

  useIonViewWillEnter(() => {
    resetValues();
  });

  useEffect(() => {
    if (!isOpen) {
      resetValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const targetEmail = selectedEmail ? selectedEmail : email;

    if (
      recoveredIdentity &&
      state.accountRecoverApproved &&
      state.accountRecoverApproved === targetEmail
    ) {
      recoverAccount(recoveredIdentity);

      dispatch({
        type: "setAccountRecoverApproved",
        accountRecoverApproved: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.accountRecoverApproved, recoveredIdentity, selectedEmail, email]);

  useEffect(() => {
    if (selectedEmail) {
      setEmail(selectedEmail);
      handleSubmit(selectedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail]);

  const resetValues = () => {
    setEmail("");
    setLoading(false);
    setIsOpenCheckEmail(false);
    setRecoveredIdentity("");

    if(setSelectedEmail) {
      setSelectedEmail('')
    }
  };

  const recoverAccount = async (identity: string) => {
    try {
      setLoading(true);
      const user: any = await getUserByIdentity(identity);

      if (user?.data?.id) {
        dispatch({ type: "recoverAccount", account: user.data });
        ShowToastSuccessMessage("You recover your identity successfully");
        dispatch({ type: "setClientReferenceId", clientReferenceId: null });

        setIsOpenCheckEmail(false);

        setIsOpen(false);
      } else {
        ShowToastErrorMessage("Recover account failed");
      }

      setLoading(false);
    } catch (e) {
      console.log(e);
      ShowToastErrorMessage("Recover account failed");
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (selectedEmail?: string) => {
    try {
      setLoading(true);

      const targetEmail = selectedEmail ? selectedEmail : email;

      const existing: any = state.accounts.find(
        (acc: any) => acc.email === targetEmail
      );

      if (existing && existing?.identity) {
        // ShowToastErrorMessage("Account already exists");
        await recoverAccount(existing?.identity);
      } else {
        const clientReferenceId = v4();
        const response: any = await requestRecoverAccount(
          targetEmail,
          clientReferenceId
        );

        // console.log("requestRecoverAccount", response);

        if (response?.status === 200 && response?.token && response?.identity) {
          dispatch({ type: "setClientReferenceId", clientReferenceId });
          setIsOpenCheckEmail(true);
          setRecoveredIdentity(response.identity);
        } else {
          ShowToastErrorMessage(response?.message || "Recover account failed.");
        }
      }

      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <IonModal
        isOpen={isOpen}
        className="recover-account-modal"
        onDidDismiss={onCloseModal}
        backdropDismiss={true}
      >
        <IonContent>
          <div className="recover-account-modal-container">
            <div className="recover-account-modal-content">
              <IonIcon icon={logoAbaxxIcon} className="modal-icon" />
              <IonText className="title">
                {selectedEmail
                  ? "Recovering Identity"
                  : "Recover your identity"}
              </IonText>
              {!selectedEmail && <p>Enter your details</p>}

              {selectedEmail ? (
                <div className="subtitle">
                  For account {formatDeletedAccountEmail(selectedEmail)}
                </div>
              ) : (
                <IonList lines="none" mode="ios">
                  <IonItem detail={false}>
                    <IonInput
                      name="email"
                      type="email"
                      inputMode="email"
                      placeholder="Enter your email"
                      value={email}
                      maxlength={144}
                      onIonChange={handleChange}
                    />
                  </IonItem>
                </IonList>
              )}

              {!selectedEmail && (
                <>
                  <IonButton
                    color="danger"
                    expand="full"
                    shape="round"
                    className="recover-account-btn"
                    disabled={!email}
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Continue
                  </IonButton>

                  <IonButton
                    fill="outline"
                    expand="full"
                    shape="round"
                    className="cancel-recover-account-btn"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                  >
                    Cancel
                  </IonButton>
                </>
              )}
            </div>
          </div>
        </IonContent>
        <IonFooter className="landing-footer">
          <div className="footer-text">
            <div className="terms-conditions-and-privacy-policy-hint">
              To learn how Verifier works without collecting your data, read our{" "}
              <span className="terms-and-conditions">Terms & Conditions</span>{" "}
              and <span className="privacy-policy">Privacy Policy</span>
            </div>
            <div className="app-version">
              © {moment().format("YYYY")} Abaxx Technologies | ver {p.version}
            </div>
          </div>
        </IonFooter>
      </IonModal>
      {loading && <IonLoading isOpen={loading} />}
      <CheckEmailModal isOpen={isOpenCheckEmail} />
    </>
  );
};

export default RecoverAccountModal;
